import type { ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export interface SkeletonProps {
  className?: string;
  /** Renders as a circle — avatars, thumbnails. */
  circle?: boolean;
}

/**
 * A shape-of-the-content placeholder.
 *
 * Skeletons over spinners for first loads: a skeleton that matches the eventual
 * layout means the page does not jump when data lands, and it communicates *what*
 * is coming. Individual skeletons are `aria-hidden` — the surrounding
 * SkeletonGroup owns the single "Loading…" announcement, so a screen reader hears
 * it once instead of eleven times.
 */
export function Skeleton({ className, circle = false }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block animate-shimmer bg-surface-sunken',
        circle ? 'rounded-full' : 'rounded-control',
        className,
      )}
    />
  );
}

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/** Several lines of fake text, last line short like real prose. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <span className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-4', index === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </span>
  );
}

export interface SkeletonGroupProps {
  /** What is loading, e.g. "Loading pricing". Announced once, politely. */
  label: string;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a skeleton layout in one live region.
 *
 * `aria-busy` plus a `role="status"` label is the pairing that makes a loading
 * state perceivable without a screen reader reading out a dozen empty boxes.
 */
export function SkeletonGroup({ label, children, className }: SkeletonGroupProps) {
  return (
    <div className={className} role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
